import { useState, useEffect } from 'react'
import { PersonStanding, Upload, Trash2, ArrowUp, ArrowDown, CheckCircle2, RefreshCw } from 'lucide-react'

interface PoseItem {
  filename: string
  enabled: boolean
}

export default function GlobalPosesPage() {
  const [poses, setPoses] = useState<PoseItem[]>([])
  const [mode, setMode] = useState<'random' | 'ordered'>('random')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')

  const load = () => {
    setLoading(true)
    fetch('/api/poses/settings')
      .then((r) => r.json())
      .then((data) => {
        setPoses(data.poses || [])
        setMode(data.mode || 'random')
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const save = async (nextPoses = poses, nextMode = mode) => {
    setSaveStatus('saving')
    try {
      await fetch('/api/poses/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: nextMode, poses: nextPoses }),
      })
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 2500)
    } catch {
      setSaveStatus('idle')
    }
  }

  const handleToggle = (filename: string) => {
    const next = poses.map((p) => p.filename === filename ? { ...p, enabled: !p.enabled } : p)
    setPoses(next)
    save(next)
  }

  const handleSelectAll = () => { const next = poses.map((p) => ({ ...p, enabled: true })); setPoses(next); save(next) }
  const handleDeselectAll = () => { const next = poses.map((p) => ({ ...p, enabled: false })); setPoses(next); save(next) }

  const handleMove = (index: number, dir: 'up' | 'down') => {
    if (dir === 'up' && index === 0) return
    if (dir === 'down' && index === poses.length - 1) return
    const target = dir === 'up' ? index - 1 : index + 1
    const next = [...poses]
    ;[next[index], next[target]] = [next[target], next[index]]
    setPoses(next)
    save(next)
  }

  const handleDelete = async (filename: string) => {
    if (!confirm(`Delete "${filename}" globally? This removes the file from the server permanently.`)) return
    try {
      const res = await fetch(`/api/poses/${encodeURIComponent(filename)}`, { method: 'DELETE' })
      if (res.ok) {
        const next = poses.filter((p) => p.filename !== filename)
        setPoses(next)
        save(next)
      }
    } catch (e) { console.error(e) }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fetch('/api/poses/upload', { method: 'POST', body: formData })
      if (res.ok) {
        const data = await res.json()
        const newFilename = data.url.split('/').pop()
        const r = await fetch('/api/poses/settings')
        const settingsData = await r.json()
        const refreshedPoses: PoseItem[] = settingsData.poses || []
        if (!refreshedPoses.some((p: PoseItem) => p.filename === newFilename)) {
          refreshedPoses.push({ filename: newFilename, enabled: true })
        }
        setPoses(refreshedPoses)
        save(refreshedPoses)
      }
    } catch (e) { console.error(e) }
    finally { setUploading(false); e.target.value = '' }
  }

  const handleModeChange = (m: 'random' | 'ordered') => {
    setMode(m)
    save(poses, m)
  }

  const enabledCount = poses.filter((p) => p.enabled).length

  return (
    <div className="p-6 max-w-5xl space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2.5">
            <PersonStanding className="h-6 w-6 text-primary" />
            Global Pose Library
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
            The default set of pose suggestions shown in the booth. Events without a custom pose list will use this library automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={load}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-[80px] justify-end">
            {saveStatus === 'saving' && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <div className="h-3 w-3 animate-spin rounded-full border border-border border-t-muted-foreground" />
                Saving...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1.5 text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Poses', value: poses.length },
          { label: 'Active', value: enabledCount },
          { label: 'Mode', value: mode === 'random' ? 'Random' : 'Ordered' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card/50 px-5 py-4">
            <div className="text-2xl font-bold text-foreground">{s.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Settings card */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-5">
        <h2 className="text-sm font-semibold text-foreground">Display Settings</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {(['random', 'ordered'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => handleModeChange(m)}
              className={`flex flex-col items-start p-4 rounded-xl border text-left transition-all ${
                mode === m
                  ? 'border-primary bg-primary/5 text-foreground ring-1 ring-primary'
                  : 'border-border bg-background text-muted-foreground hover:bg-muted/30'
              }`}
            >
              <span className="font-semibold text-sm text-foreground">
                {m === 'random' ? 'Randomised Order' : 'Specific Order'}
              </span>
              <span className="text-xs mt-1 text-muted-foreground">
                {m === 'random'
                  ? 'Randomly shuffles enabled poses for every session.'
                  : 'Shows poses in the exact order set below.'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Pose library */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
          <div>
            <h2 className="text-base font-semibold">Pose Files</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Toggle and reorder poses. Changes auto-save instantly.
            </p>
            {poses.length > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <button onClick={handleSelectAll} className="text-[11px] font-semibold text-primary hover:underline">Enable All</button>
                <span className="text-muted-foreground/30 text-[10px] select-none">|</span>
                <button onClick={handleDeselectAll} className="text-[11px] font-semibold text-muted-foreground hover:text-foreground">Disable All</button>
              </div>
            )}
          </div>
          <label className={`cursor-pointer rounded-lg border border-border bg-secondary px-4 py-2 text-xs font-semibold hover:bg-secondary/80 transition-colors flex items-center gap-2 shrink-0 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            <Upload className="h-3.5 w-3.5" />
            {uploading ? 'Uploading...' : 'Upload Pose (PNG)'}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-primary" />
            Loading poses...
          </div>
        ) : poses.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-muted-foreground bg-card/20">
            <PersonStanding className="h-8 w-8 opacity-30 mb-3" />
            <p className="text-sm font-semibold">No poses in the library yet</p>
            <p className="text-xs mt-1 opacity-60">Upload transparent PNG files to get started.</p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {poses.map((p, idx) => (
              <div
                key={p.filename}
                className={`group relative flex flex-col rounded-xl border transition-all p-3 bg-card ${
                  p.enabled ? 'border-border/80 hover:border-primary/50' : 'border-border/40 opacity-50 hover:opacity-80'
                }`}
              >
                <div className="relative aspect-[3/4] w-full rounded-lg bg-muted/40 border border-border/20 overflow-hidden flex items-center justify-center p-2">
                  <img src={`/poses/${p.filename}`} className="h-full w-full object-contain drop-shadow" alt="" />
                  <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => handleDelete(p.filename)}
                      className="rounded-md bg-destructive/10 p-1.5 text-destructive hover:bg-destructive/20 border border-destructive/20"
                      title="Delete globally"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={p.enabled}
                    onChange={() => handleToggle(p.filename)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer bg-background"
                    id={`global-pose-${p.filename}`}
                  />
                  <label
                    htmlFor={`global-pose-${p.filename}`}
                    className="text-xs font-medium truncate flex-1 cursor-pointer select-none text-foreground/90"
                    title={p.filename}
                  >
                    {p.filename.replace(/\.[^/.]+$/, '')}
                  </label>
                </div>
                {mode === 'ordered' && (
                  <div className="mt-2.5 pt-2.5 border-t border-border/40 flex justify-between gap-1">
                    <button
                      type="button"
                      onClick={() => handleMove(idx, 'up')}
                      disabled={idx === 0}
                      className="flex-1 flex items-center justify-center gap-1 py-1 rounded bg-secondary hover:bg-secondary/80 text-[10px] font-semibold disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMove(idx, 'down')}
                      disabled={idx === poses.length - 1}
                      className="flex-1 flex items-center justify-center gap-1 py-1 rounded bg-secondary hover:bg-secondary/80 text-[10px] font-semibold disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
